import { useMemo } from 'react';
import { GridColDef } from '@mui/x-data-grid';
import { Chip } from '@mui/material';
import { useTags } from '../hooks/useTags';
import { TagFilters, TagGridRow } from '../types';
import { filterTagsGridRows } from '../utils/filterUtils';
import TagRowActions from './TagRowActions';
import { tagsValueSetConfig, getCodeSystemColor as getColorFromConfig } from '../config/valuesetConfig';
import { WrappedValueSet } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import DataGridWithFilters from 'src/careflow/ph-core/grid/react/DataGridWithFilters/DataGridWithFilters';

interface TagsGridProps {
  filters: TagFilters;
  search: string;
  onRowClick: (tagId: string) => void;
  onEdit: (tagId: string) => void;
  onFilterDrawerOpen: Function;
  onSearchTextFieldChange: (value: string) => void;
  searchTextFieldValue: string | null;
  onNewTag: () => void;
}

function getCodeSystemColor(codeSystem: string): string {
  return getColorFromConfig(tagsValueSetConfig, codeSystem);
}

export default function TagsGrid({
  filters,
  search,
  onRowClick,
  onEdit,
  onFilterDrawerOpen,
  onSearchTextFieldChange,
  searchTextFieldValue,
  onNewTag,
}: TagsGridProps) {
  const { data: tags, isLoading, isError, error } = useTags(filters, search);

  const rows: TagGridRow[] = useMemo(() => {
    if (!tags || tags.length === 0) {
      return [];
    }
    
    // Flatten: Each ValueSet can have multiple concepts, so create one row per concept
    const mappedRows: TagGridRow[] = [];
    
    (tags as WrappedValueSet[])
      .filter((tag) => tag && tag.id) // Filter out tags without IDs
      .forEach((tag: WrappedValueSet) => {
        // Call wrapper methods directly - they should be bound to the tag object
        let concepts: any[] = [];
        let system = '';
        
        try {
          // Wrapped ValueSet should have asListAll method
          if (tag.asListAll && typeof tag.asListAll === 'function') {
            concepts = tag.asListAll();
          } else if (tag.compose?.include) {
            // Fallback: manually extract concepts if wrapper not applied
            concepts = tag.compose.include.flatMap((include: any) => 
              (include.concept || []).map((concept: any) => ({
                code: concept.code,
                display: concept.display,
                system: include.system,
              }))
            );
          }
          
          // Wrapped ValueSet should have getConceptSystem method
          if (tag.getConceptSystem && typeof tag.getConceptSystem === 'function') {
            system = tag.getConceptSystem() || '';
          } else if (tag.compose?.include?.[0]?.system) {
            system = tag.compose.include[0].system;
          }
        } catch (err) {
          console.error('Error processing tag:', err, tag);
        }
        
        const codeSystemId = system.split('/').pop() || '';
        
        // CodeSystem name: Since backend always creates CodeSystem with name "OrganizationTags"
        // We display "Organization Tags" as the friendly name
        const codeSystemName = 'Organization Tags';
        
        // Extract resources from meta.tag
        const resources: string[] = [];
        if (tag.meta?.tag) {
          tag.meta.tag.forEach((tagMeta: any) => {
            if (tagMeta.code) {
              resources.push(tagMeta.code);
            }
          });
        }
        
        // Create one row per concept in the ValueSet
        if (concepts.length > 0) {
          concepts.forEach((concept, index) => {
            mappedRows.push({
              id: `${tag.id}-${concept.code}-${index}`, // Unique ID per concept for DataGrid
              valueSetId: tag.id || '', // ValueSet ID for actions (view/edit)
              display: concept.display || concept.code || '',
              code: concept.code || '',
              codeSystem: codeSystemName,
              resources: resources.length > 0 ? resources.join(', ') : '-',
              valueSet: tag as WrappedValueSet,
            });
          });
        } else {
          // Fallback: if no concepts found, create one row with ValueSet info
          mappedRows.push({
            id: tag.id || '',
            valueSetId: tag.id || '',
            display: tag.title || tag.name || tag.id || '',
            code: '',
            codeSystem: codeSystemName,
            resources: resources.length > 0 ? resources.join(', ') : '-',
            valueSet: tag as WrappedValueSet,
          });
        }
      });
    
    return mappedRows;
  }, [tags]);

  const filteredRows = useMemo(
    () => filterTagsGridRows(rows, { filters, searchTextFieldValue: search }),
    [rows, filters, search]
  );

  const columns: GridColDef[] = [
    {
      field: 'display',
      headerName: 'Display',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'code',
      headerName: 'Code',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'codeSystem',
      headerName: 'Code System',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          sx={{
            backgroundColor: getCodeSystemColor(params.value),
            color: 'white',
            fontWeight: 'bold',
          }}
        />
      ),
    },
    {
      field: 'resources',
      headerName: 'Resources',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'actions',
      headerName: '',
      width: 80,
      sortable: false,
      renderCell: (params) => (
        <TagRowActions
          tagId={params.row.valueSetId}
          onEdit={onEdit}
          onView={() => onRowClick(params.row.valueSetId)}
        />
      ),
    },
  ];

  return (
    <DataGridWithFilters
      rows={filteredRows}
      columns={columns}
      loading={isLoading}
      onRowDoubleClick={(params) => onRowClick(params.row.valueSetId)}
      showAddButton={false}
      addButtonTitle=""
      onAddButtonClick={() => {}}
      onFilterDrawerOpen={onFilterDrawerOpen}
      onSearchTextFieldChange={onSearchTextFieldChange}
      searchTextFieldValue={searchTextFieldValue}
      searchPlaceholder="Search Tag..."
      Filters={null}
      autoHeight
      disableExtendRowFullWidth={false}
    />
  );
}

